import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play, Rotate3d, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  return m ? m[1] : null;
};

const VideoFacade = ({ url, poster, posterAlt, icon, playLabel, note, dark = false, testId, title }) => {
  const [playing, setPlaying] = useState(false);
  const id = ytId(url);

  if (playing && id) {
    return (
      <div className="relative overflow-hidden photo-frame" style={{ aspectRatio: "16/9" }} data-testid={testId}>
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setPlaying(true)}
      data-testid={testId}
      aria-label={playLabel}
      className="group relative block w-full overflow-hidden photo-frame text-left"
      style={{ aspectRatio: "16/9" }}
    >
      <img src={poster} alt={posterAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]" />
      <div className={`absolute inset-0 ${dark ? "bg-black/50" : "bg-black/40"} group-hover:bg-black/30 transition-colors duration-700`} />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <span className="w-16 h-16 rounded-full border border-[#C5A059] flex items-center justify-center text-[#C5A059] mb-5 transition-all duration-500 group-hover:bg-[#C5A059] group-hover:text-[#121212] group-hover:scale-105">
          {icon}
        </span>
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#F7F5F0] font-medium">{playLabel}</p>
        {note && <p className="text-xs text-[#F7F5F0]/60 mt-3 max-w-md">{note}</p>}
      </div>
    </button>
  );
};

const VillagePage = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const { data: village, loading, error } = useApi(`/villages/${slug}`);
  const { data: villages } = useApi("/villages");
  const [copied, setCopied] = useState(false);

  const text = village ? t(`villagesText.${village.slug}`) : null;
  useSeo("village", { name: village?.name || "" });

  const { prev, next, index } = useMemo(() => {
    if (!villages || !village) return {};
    const i = villages.findIndex((v) => v.slug === village.slug);
    return {
      index: i,
      prev: villages[(i - 1 + villages.length) % villages.length],
      next: villages[(i + 1) % villages.length],
    };
  }, [villages, village]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${village.name} — Monferrato Immersive`, url }); return; } catch { /* annullato */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success(t("village.copiedToast"));
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return <div className="h-screen bg-[#121212] animate-pulse" data-testid="village-loading" />;
  }
  if (error || !village) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="village-not-found">
        <h1 className="font-serif-display text-4xl">{t("village.notFound")}</h1>
        <Link to="/#esplora" className="mt-8 text-[11px] tracking-[0.25em] uppercase text-[#9E7B36] editorial-link">{t("village.backToBorghi")}</Link>
      </div>
    );
  }

  const tagline = text?.tagline || village.tagline;

  return (
    <div data-testid="village-page">
      <PageHero
        testId="village-hero"
        image={village.heroImage}
        eyebrow={t("village.borgoOf", { n: String(index + 1).padStart(2, "0") })}
        lines={[village.name]}
        subtitle={tagline}
        height="h-[85vh]"
      />

      {/* SCOPRI IL BORGO */}
      <section className="py-20 lg:py-32" data-testid="village-story-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHead eyebrow={t("village.storyEyebrow")} title={tagline} />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-[#666157]">
                {t("village.storyWip", { name: village.name })} {text?.short || village.shortDescription}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <div className={`grid gap-5 ${village.gallery.length > 1 ? "grid-cols-2 items-start" : ""}`}>
                {village.gallery.map((img, i) => (
                  <img
                    key={img}
                    src={img}
                    alt={`${village.name} — ${t("village.galleryAlt")} ${i + 1}`}
                    loading="lazy"
                    className={`w-full object-cover photo-frame ${i === 1 ? "mt-12" : ""}`}
                    style={{ aspectRatio: village.gallery.length > 1 ? "4/5" : "16/9" }}
                  />
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GUARDA IL FILM */}
      <section className="py-20 lg:py-28" data-testid="village-film-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead eyebrow={t("village.filmEyebrow")} title={t("village.filmTitle")} />
          <Reveal delay={0.15}>
            <div className="mt-12">
              <VideoFacade
                url={village.cinematicVideoUrl}
                poster={village.heroImage}
                posterAlt={`${village.name} — ${t("village.filmAlt")}`}
                icon={<Play size={22} className="ml-1" />}
                playLabel={t("village.filmPlay")}
                note={t("village.filmNote")}
                testId="village-film-player"
                title={`${village.name} — ${t("village.filmTitle")}`}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VIVI IL BORGO A 360° — mostrata solo quando il video 360° ufficiale esiste */}
      {village.video360Url && (
        <section className="py-20 lg:py-28 bg-[#121212] text-[#F7F5F0]" data-testid="village-360-section">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <SectionHead dark eyebrow={t("village.panoEyebrow")} title={t("village.panoTitle")} />
              <Reveal delay={0.15}>
                <p className="mt-7 text-[#F7F5F0]/65 leading-relaxed text-base">{t("village.panoNote")}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.2}>
                <div className="border border-[#C5A059]/30 p-1.5">
                  <VideoFacade
                    url={village.video360Url}
                    poster={village.heroImage}
                    posterAlt={`${village.name} — ${t("village.panoAlt")}`}
                    icon={<Rotate3d size={24} strokeWidth={1.4} />}
                    playLabel={t("village.panoPlay")}
                    dark
                    testId="village-360-player"
                    title={`${village.name} — 360°`}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* LUOGHI DA SCOPRIRE */}
      {village.places?.length > 0 && (
        <section className="py-20 lg:py-28" data-testid="village-places-list">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <SectionHead eyebrow="Luoghi da scoprire" title="Dove posare lo sguardo" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {village.places.map((p, i) => (
                <Reveal key={p.name} delay={0.08 * i}>
                  <div className="border-t border-[#C5A059]/40 pt-6">
                    <h3 className="font-serif-display text-xl">{p.name}</h3>
                    <p className="text-sm text-[#666157] mt-3 leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LA MUSICA DEL VIAGGIO — solo se esiste un'associazione ufficiale */}
      {village.soundtrack && (
        <section className="py-20 lg:py-28 bg-[#0E0D0C] text-[#F7F5F0]" data-testid="village-soundtrack-card">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <SectionHead dark eyebrow="La musica del viaggio" title={village.soundtrack.title} />
          </div>
        </section>
      )}

      {/* CONDIVIDI + PREV/NEXT */}
      <section className="py-16 border-t border-[#1C1B18]/10" data-testid="village-pagination-nav">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex justify-center mb-14">
            <button
              onClick={share}
              data-testid="village-share-button"
              className="inline-flex items-center gap-3 border border-[#1C1B18]/25 px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#9E7B36] transition-colors duration-500"
            >
              {copied ? <Check size={14} className="text-[#9E7B36]" /> : <Share2 size={14} />}
              {copied ? t("village.copiato") : t("village.condividi")}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {prev && (
              <Link to={`/borghi/${prev.slug}`} data-testid="village-prev-link" className="group text-left border-r border-[#1C1B18]/10 pr-6">
                <span className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#666157]"><ArrowLeft size={12} /> {t("village.prev")}</span>
                <span className="font-serif-display text-2xl lg:text-4xl mt-3 block group-hover:text-[#9E7B36] transition-colors duration-500">{prev.name}</span>
              </Link>
            )}
            {next && (
              <Link to={`/borghi/${next.slug}`} data-testid="village-next-link" className="group text-right">
                <span className="flex items-center gap-2 justify-end text-[10px] tracking-[0.3em] uppercase text-[#666157]">{t("village.next")} <ArrowRight size={12} /></span>
                <span className="font-serif-display text-2xl lg:text-4xl mt-3 block group-hover:text-[#9E7B36] transition-colors duration-500">{next.name}</span>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VillagePage;
